import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

const ProcessPageDemo = () => {
  const { processName } = useParams();
  const [sidebarData, setSidebarData] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [loading, setLoading] = useState(true);

  // Convert URL slug back to folder name
  const getFolderName = (slug) => {
    return slug.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  // Demo data to show the structure
  const demoData = {
    'applications': [
      {
        folder: { id: '1', name: 'Backend Services' },
        documents: [
          { id: 'doc1', name: 'API Design Guidelines', mimeType: 'application/vnd.google-apps.document' },
          { id: 'doc2', name: 'Database Schema', mimeType: 'application/vnd.google-apps.spreadsheet' },
          { id: 'doc3', name: 'Service Architecture', mimeType: 'application/vnd.google-apps.presentation' }
        ]
      },
      {
        folder: { id: '2', name: 'Frontend Applications' },
        documents: [
          { id: 'doc4', name: 'Component Library', mimeType: 'application/vnd.google-apps.document' },
          { id: 'doc5', name: 'State Management', mimeType: 'application/vnd.google-apps.document' }
        ]
      }
    ],
    'infrastructure': [
      {
        folder: { id: '3', name: 'AWS Resources' },
        documents: [
          { id: 'doc6', name: 'EKS Configuration', mimeType: 'application/vnd.google-apps.document' },
          { id: 'doc7', name: 'VPC Setup', mimeType: 'application/vnd.google-apps.spreadsheet' }
        ]
      }
    ],
    'security': [
      {
        folder: { id: '4', name: 'Access Control' },
        documents: [
          { id: 'doc8', name: 'IAM Policies', mimeType: 'application/vnd.google-apps.document' },
          { id: 'doc9', name: 'Security Checklist', mimeType: 'application/vnd.google-apps.spreadsheet' }
        ]
      }
    ]
  };

  useEffect(() => {
    // Simulate loading from Google Drive
    const timer = setTimeout(() => {
      const processData = demoData[processName] || [];
      setSidebarData(processData);
      setLoading(false);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [processName]);

  const handleDocumentSelect = (document) => {
    setSelectedDocument(document);
  };

  const getDocumentIcon = (mimeType) => {
    switch (mimeType) {
      case 'application/vnd.google-apps.spreadsheet':
        return 'M19,11H5V9H19M19,7H5V5H19M5,15H19V13H5M5,19H19V17H5V19Z';
      case 'application/vnd.google-apps.presentation':
        return 'M2,16V4A2,2 0 0,1 4,2H20A2,2 0 0,1 22,4V16A2,2 0 0,1 20,18H4A2,2 0 0,1 2,16M4,4V16H20V4H4M6,6H18V8H6V6M6,10H14V12H6V10M6,14H16V16H6V14Z';
      default:
        return 'M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z';
    }
  };

  if (loading) {
    return (
      <div className="process-page loading">
        <h2>Loading {getFolderName(processName)}...</h2>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="process-page">
      <div className="workspace-container">
        
        {/* Sidebar */}
        <div className="sidebar">
          <div className="sidebar-content">
            {sidebarData.length === 0 ? (
              <div className="no-documents">
                <p>No documents found in this process</p>
              </div>
            ) : (
              sidebarData.map((section) => (
                <div key={section.folder.id} className="sidebar-section">
                  
                  <div className="sidebar-header">
                    <span>{section.folder.name}</span>
                    <span className="sidebar-icon expanded">▶</span>
                  </div>

                  <div className="sidebar-items">
                    {section.documents.map((document) => (
                      <div 
                        key={document.id}
                        className={`document-item ${selectedDocument?.id === document.id ? 'active' : ''}`}
                        onClick={() => handleDocumentSelect(document)}
                      >
                        <svg className="doc-icon" viewBox="0 0 24 24" fill="currentColor">
                          <path d={getDocumentIcon(document.mimeType)} />
                        </svg>
                        <span className="document-name">{document.name}</span>
                      </div>
                    ))}
                  </div>

                </div>
              ))
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="workspace-content">
          {selectedDocument ? (
            <div className="document-viewer">
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <h3>📄 {selectedDocument.name}</h3>
                <p style={{ color: '#666', marginBottom: '30px' }}>
                  In production, this would show the Google Doc/Sheet/Slides embedded here
                </p>
                
                <div style={{ 
                  background: '#f5f5f5', 
                  border: '2px dashed #ccc', 
                  height: '400px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  borderRadius: '8px'
                }}>
                  <div>
                    <svg className="doc-icon" viewBox="0 0 24 24" fill="currentColor" style={{ width: '48px', height: '48px', marginBottom: '15px', color: '#999' }}>
                      <path d={getDocumentIcon(selectedDocument.mimeType)} />
                    </svg>
                    <p style={{ color: '#999' }}>Google Document Embed Area</p>
                    <p style={{ color: '#666', fontSize: '14px' }}>
                      Type: {selectedDocument.mimeType.split('.').pop()}
                    </p>
                  </div>
                </div>

                <div style={{ marginTop: '30px', padding: '20px', background: '#e8f5e8', borderRadius: '8px' }}>
                  <h4>🚀 React Benefits Here:</h4>
                  <ul style={{ textAlign: 'left', marginTop: '10px' }}>
                    <li><strong>Component Reuse</strong>: This DocumentViewer component works for all document types</li>
                    <li><strong>State Management</strong>: Click tracking and active states handled automatically</li>
                    <li><strong>Clean Code</strong>: Just 60 lines vs 100+ lines of inline HTML/JS</li>
                    <li><strong>Type Safety</strong>: Easy to add TypeScript for better error catching</li>
                  </ul>
                </div>
              </div>
            </div>
          ) : (
            <div className="welcome-message">
              <h2>{getFolderName(processName)}</h2>
              <p>Select a document from the sidebar to view its content.</p>
              {sidebarData.length === 0 && (
                <p><em>No documents found in this process folder.</em></p>
              )}
              
              <div style={{ marginTop: '40px', padding: '20px', background: '#f0f7ff', borderRadius: '8px' }}>
                <h3>📋 Current Process: {getFolderName(processName)}</h3>
                <p>This page was dynamically generated based on the URL parameter!</p>
                <p><strong>URL slug:</strong> {processName}</p>
                <p><strong>Folder name:</strong> {getFolderName(processName)}</p>
                <p style={{ marginTop: '15px', fontSize: '14px', color: '#666' }}>
                  In the HTML/JS version, you needed separate files for each process. 
                  In React, one component handles all processes dynamically! 🎉
                </p>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default ProcessPageDemo;